import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getApiUrl } from '../utils/api';
import { useSSE, getWorkerUrl } from '../utils/useSSE';
import './NotificationBlock.css';

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

function NotificationBlock({ user, powerUserMode, msalInstance }) {
  const { t } = useTranslation();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const quillRef = useRef(null);

  // Quill editor configuration - lightweight toolbar with bold, font size, and paragraphs
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold'],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold',
    'size',
    'align',
  ];

  // Helper to check if content is HTML or plain text
  const isHTML = (str) => {
    if (!str) return false;
    const htmlRegex = /<[a-z][\s\S]*>/i;
    return htmlRegex.test(str);
  };

  // Helper to safely render notification message (handles both HTML and plain text)
  const renderNotificationMessage = (message) => {
    if (!message) return '';
    if (isHTML(message)) {
      return { __html: message };
    }
    // Plain text - convert newlines to paragraphs
    const paragraphs = message.split('\n').filter(p => p.trim());
    return { __html: paragraphs.map(p => `<p>${p}</p>`).join('') };
  };

  // Load notification on mount
  useEffect(() => {
    const loadNotification = async () => {
      try {
        const idToken = await getIdToken(msalInstance);
        if (!idToken) {
          setLoading(false);
          return;
        }

        const response = await fetch(getApiUrl('/api/notifications'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setNotification(data);
          if (data) {
            setFormMessage(data.message);
          }
        }
      } catch (error) {
        console.error('Failed to load notification:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNotification();
  }, [msalInstance]);

  // Listen for SSE updates
  const workerUrl = getWorkerUrl();
  useSSE(workerUrl, (data) => {
    console.log('SSE: Notification received:', data);
    // Only handle notification events
    if (data.type === 'notifications') {
      if (data.data?.action === 'created') {
        setNotification(data.data.data);
        setFormMessage(data.data.data?.message || '');
        setShowForm(false);
      } else if (data.data?.action === 'deleted') {
        setNotification(null);
        setFormMessage('');
        setShowForm(false);
      }
    }
  });

  const handleDelete = async () => {
    if (!powerUserMode || !user?.isPowerUser) {
      return;
    }

    if (!window.confirm(t('notifications.deleteConfirm'))) {
      return;
    }

    setIsDeleting(true);
    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      const response = await fetch(getApiUrl('/api/notifications'), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('notifications.failedToDelete'));
      }

      setNotification(null);
      setFormMessage('');
      setShowForm(false);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      alert(t('notifications.failedToDelete') + ': ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!powerUserMode || !user?.isPowerUser) {
      return;
    }

    // Get HTML content from Quill editor
    const quill = quillRef.current?.getEditor();
    const htmlContent = quill ? quill.root.innerHTML : formMessage;
    const textContent = quill ? quill.getText().trim() : formMessage.trim();
    
    if (!textContent) {
      alert(t('notifications.messageRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('auth.failedToGetToken'));
      }

      const response = await fetch(getApiUrl('/api/notifications'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: htmlContent }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('notifications.failedToSave'));
      }

      const result = await response.json();
      setNotification(result.notification);
      setShowForm(false);
    } catch (error) {
      console.error('Failed to save notification:', error);
      alert(t('notifications.failedToSave') + ': ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    if (notification) {
      setFormMessage(notification.message || '');
    } else {
      setFormMessage('');
    }
  };

  // Initialize form message when opening form
  useEffect(() => {
    if (showForm) {
      if (notification && notification.message) {
        setFormMessage(notification.message);
      } else {
        setFormMessage('');
      }
    }
  }, [showForm, notification]);

  const canManage = powerUserMode && user?.isPowerUser;

  // Don't render anything if loading and not a PowerUser
  if (loading && !canManage) {
    return null;
  }

  // Don't render anything if no notification and not a PowerUser
  if (!loading && !notification && !canManage) {
    return null;
  }

  return (
    <>
      {canManage && (
        <div className="notification-manager">
          {!showForm ? (
            <button
              className="notification-manage-button"
              onClick={() => setShowForm(true)}
              title={notification ? t('notifications.edit') : t('notifications.create')}
            >
              {notification ? '✏️ ' + t('notifications.edit') : '➕ ' + t('notifications.create')}
            </button>
          ) : (
            <form className="notification-form" onSubmit={handleSave}>
              <div className="notification-form-header">
                <h3>{notification ? t('notifications.edit') : t('notifications.create')}</h3>
              </div>
              <div className="notification-form-body">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formMessage}
                  onChange={setFormMessage}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={t('notifications.messagePlaceholder')}
                  className="notification-quill-editor"
                />
              </div>
              <div className="notification-form-actions">
                <button
                  type="submit"
                  className="notification-form-save"
                  disabled={isSaving}
                >
                  {isSaving ? t('common.loading') : t('common.save')}
                </button>
                <button
                  type="button"
                  className="notification-form-cancel"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  {t('common.cancel')}
                </button>
                {notification && (
                  <button
                    type="button"
                    className="notification-form-delete"
                    onClick={handleDelete}
                    disabled={isDeleting || isSaving}
                  >
                    {isDeleting ? '...' : t('common.delete')}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
      {notification && !showForm && (
        <div className="notification-block">
          <div className="notification-content">
            <div className="notification-icon">📢</div>
            <div 
              className="notification-message"
              dangerouslySetInnerHTML={renderNotificationMessage(notification.message)}
            />
            {canManage && (
              <button
                className="notification-delete"
                onClick={handleDelete}
                disabled={isDeleting}
                title={t('notifications.delete')}
                aria-label={t('notifications.delete')}
              >
                {isDeleting ? '...' : '×'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default NotificationBlock;
